import React, { useState, useEffect } from 'react';
import { Save, X, Trash2, Plus, Users, User, MapPin, Phone, Mail, DollarSign, GripVertical } from 'lucide-react';
import { JobberService } from '../services/jobberService';
import syncService from '../services/syncService';
import SyncSuccessPopup from './SyncSuccessPopup';
import DisconnectionBubble from './DisconnectionBubble';

// Derive initial structures from quote (match iOS: prefer structures, else one "Main" from lineItems)
function getInitialStructures(quote) {
  if (quote?.structures?.length > 0) {
    return quote.structures.map(s => ({
      id: s.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`),
      name: s.name || 'Main',
      lineItems: (s.lineItems || []).map(li => ({
        id: li.id || `li_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        windowType: li.windowType || '',
        counts: li.counts || '',
        totalCount: li.totalCount ?? 0,
        price: li.price ?? 0,
        pricePerUnitOverride: li.pricePerUnitOverride
      }))
    }));
  }
  const lineItems = quote?.lineItems || [];
  return [{
    id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `s_${Date.now()}`,
    name: 'Main',
    lineItems: lineItems.map(li => ({
      id: li.id || `li_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      windowType: li.windowType || '',
      counts: li.counts || '',
      totalCount: li.totalCount ?? 0,
      price: li.price ?? 0,
      pricePerUnitOverride: li.pricePerUnitOverride
    }))
  }];
}

const QuoteCreator = ({ quote = null, onSave, onCancel, windowTypes, customerTypes, isDarkMode }) => {
  // Helper function to migrate name to firstName/lastName
  const migrateCustomerName = (customerData) => {
    if (customerData.firstName && customerData.lastName) {
      // Already migrated
      return customerData;
    }
    
    if (customerData.name) {
      const nameParts = customerData.name.trim().split(/\s+/);
      if (nameParts.length === 1) {
        return {
          ...customerData,
          firstName: nameParts[0],
          lastName: '',
          name: customerData.name // Keep original for backward compatibility
        };
      } else {
        return {
          ...customerData,
          firstName: nameParts.slice(0, -1).join(' '),
          lastName: nameParts[nameParts.length - 1],
          name: customerData.name // Keep original for backward compatibility
        };
      }
    }
    
    // Ensure firstName and lastName are always strings (never undefined)
    return {
      ...customerData,
      firstName: customerData.firstName ?? '',
      lastName: customerData.lastName ?? '',
      name: customerData.name || ''
    };
  };

  const [customer, setCustomer] = useState(() => {
    const initialCustomer = {
      name: quote?.customer?.name || '',
      address: quote?.customer?.address || '',
      phone: quote?.customer?.phone || '',
      email: quote?.customer?.email || '',
      type: quote?.customer?.type || 'Residential'
    };
    return migrateCustomerName(initialCustomer);
  });

  const [structures, setStructures] = useState(() => getInitialStructures(quote));
  const [showWindowTypePopup, setShowWindowTypePopup] = useState(false);
  const [addLineItemToStructureIdx, setAddLineItemToStructureIdx] = useState(null);
  const [showContactImport, setShowContactImport] = useState(false);
  const [contactImportText, setContactImportText] = useState('');
  const [showExtraDirtyOptions, setShowExtraDirtyOptions] = useState(false);
  const [notes, setNotes] = useState(quote?.notes ?? '');
  const [scrollToLineItemId, setScrollToLineItemId] = useState(null);
  const [editPriceFor, setEditPriceFor] = useState(null); // { structureIdx, item }
  const [editPriceInput, setEditPriceInput] = useState('');
  const [draggedLineItemId, setDraggedLineItemId] = useState(null);
  const [dragOverLineItemId, setDragOverLineItemId] = useState(null);
  
  // Sync feedback state
  const [showSyncSuccess, setShowSyncSuccess] = useState(false);
  const [showDisconnectionBubble, setShowDisconnectionBubble] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(quote?.userId || null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    exteriorPercentage: 60,
    interiorPercentage: 40,
    showExterior: true,
    showInterior: true,
    extraDirtyFee: 0,
    extraDirtyIsPercentage: false,
    extraDirtyApplyTo: 'all',
    ...(quote?.settings || {})
  });

  // Helper function to calculate base price for a line item (matches iOS logic)
  const getBasePriceForLineItem = (lineItem) => {
    // Priority: pricePerUnitOverride > customer type price > window type default price
    if (lineItem.pricePerUnitOverride != null) {
      return lineItem.pricePerUnitOverride;
    }
    const customerType = customerTypes.find(c => c.name === customer.type);
    return customerType?.windowPrices?.[lineItem.windowType] ?? windowTypes.find(w => w.name === lineItem.windowType)?.price ?? 0;
  };

  // Recalculate line item prices when customer type changes (Feature #4)
  useEffect(() => {
    // Only update if we have line items and customer type is set
    if (!customer.type || structures.every(s => s.lineItems.length === 0)) {
      return;
    }

    setStructures(prev => prev.map(structure => ({
      ...structure,
      lineItems: structure.lineItems.map(item => {
        // Only recalculate if there's no price override (matches iOS behavior)
        if (item.pricePerUnitOverride != null) {
          return item; // Keep override, don't recalculate
        }
        
        const basePrice = getBasePriceForLineItem(item);
        const newPrice = item.totalCount * basePrice;
        
        // Only update if price actually changed to avoid infinite loops
        if (Math.abs(item.price - newPrice) < 0.01) {
          return item;
        }
        
        return {
          ...item,
          price: newPrice
        };
      })
    })));
    // Intentionally omit getBasePriceForLineItem and structures to avoid infinite loops (effect updates structures)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.type]);

  // Feature #3: Auto scroll to new window type tile after add
  useEffect(() => {
    if (!scrollToLineItemId) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-line-item-id="${scrollToLineItemId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setScrollToLineItemId(null);
    }, 150);
    return () => clearTimeout(timer);
  }, [scrollToLineItemId]);

  const updateStructure = (sIdx, updater) => {
    setHasUnsavedChanges(true);
    setStructures(prev => prev.map((s, i) => (i === sIdx ? updater(s) : s)));
  };

  const addLineItem = (structureIdx, windowType) => {
    setHasUnsavedChanges(true);
    const newId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `li_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    updateStructure(structureIdx, s => ({
      ...s,
      lineItems: [...s.lineItems, { id: newId, windowType, counts: '', totalCount: 0, price: 0 }]
    }));
    setAddLineItemToStructureIdx(null);
    setShowWindowTypePopup(false);
    setScrollToLineItemId(newId); // Feature #3: scroll to new tile after add
  };

  const updateLineItem = (structureIdx, lineItemId, field, value) => {
    setHasUnsavedChanges(true);
    updateStructure(structureIdx, s => {
      const item = s.lineItems.find(i => i.id === lineItemId);
      if (!item) return s;
      const updated = { ...item, [field]: value };
      if (field === 'counts') {
        const numbers = String(value).split(' ').filter(n => n.trim() && !isNaN(n.trim()));
        const total = numbers.reduce((sum, n) => sum + parseInt(n.trim(), 10), 0);
        updated.totalCount = total;
        const basePrice = getBasePriceForLineItem(item);
        updated.price = total * basePrice;
      }
      return {
        ...s,
        lineItems: s.lineItems.map(i => (i.id === lineItemId ? updated : i))
      };
    });
  };

  const removeLineItem = (structureIdx, lineItemId) => {
    setHasUnsavedChanges(true);
    updateStructure(structureIdx, s => ({ ...s, lineItems: s.lineItems.filter(i => i.id !== lineItemId) }));
  };

  // Feature #2: Set or clear price per unit override for a line item
  // Feature #11: Reorder line items within a structure
  const handleLineItemDrop = (structureIdx, targetLineItemId, droppedLineItemId) => {
    if (targetLineItemId === droppedLineItemId) return;
    const structure = structures[structureIdx];
    const items = [...structure.lineItems];
    const draggedIndex = items.findIndex(i => i.id === droppedLineItemId);
    const targetIndex = items.findIndex(i => i.id === targetLineItemId);
    if (draggedIndex === -1 || targetIndex === -1) return;
    const [draggedItem] = items.splice(draggedIndex, 1);
    items.splice(targetIndex, 0, draggedItem);
    setHasUnsavedChanges(true);
    updateStructure(structureIdx, s => ({ ...s, lineItems: items }));
    setDraggedLineItemId(null);
    setDragOverLineItemId(null);
  };

  const saveEditPrice = (structureIdx, lineItemId, pricePerUnitOverride) => {
    setHasUnsavedChanges(true);
    updateStructure(structureIdx, s => {
      const item = s.lineItems.find(i => i.id === lineItemId);
      if (!item) return s;
      const basePrice = pricePerUnitOverride ?? getBasePriceForLineItem({ ...item, pricePerUnitOverride: undefined });
      const newPrice = item.totalCount * basePrice;
      return {
        ...s,
        lineItems: s.lineItems.map(i =>
          i.id === lineItemId
            ? { ...i, pricePerUnitOverride: pricePerUnitOverride ?? undefined, price: newPrice }
            : i
        )
      };
    });
    setEditPriceFor(null);
    setEditPriceInput('');
  };

  const addStructure = () => {
    setHasUnsavedChanges(true);
    const newId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    setStructures(prev => [...prev, { id: newId, name: 'New Building', lineItems: [] }]);
  };

  const removeStructure = (sIdx) => {
    if (structures.length <= 1) return;
    setHasUnsavedChanges(true);
    setStructures(prev => prev.filter((_, i) => i !== sIdx));
  };

  const setStructureName = (sIdx, name) => {
    setHasUnsavedChanges(true);
    updateStructure(sIdx, s => ({ ...s, name }));
  };

  const parseContactText = (contactText) => {
    try {
      const lines = contactText.split('\n').map(line => line.trim()).filter(line => line);
      let name = '';
      let email = '';
      let phone = '';
      let address = '';

      lines.forEach(line => {
        const lowerLine = line.toLowerCase();
        if (lowerLine.startsWith('name:')) {
          name = line.substring(5).trim();
        } else if (lowerLine.startsWith('email:')) {
          email = line.substring(6).trim();
        } else if (lowerLine.startsWith('phone:')) {
          phone = line.substring(6).trim();
        } else if (lowerLine.startsWith('address:')) {
          address = line.substring(8).trim();
        }
      });

      // Parse name into firstName and lastName
      let firstName = '';
      let lastName = '';
      if (name) {
        const nameParts = name.trim().split(/\s+/);
        if (nameParts.length === 1) {
          firstName = nameParts[0];
          lastName = '';
        } else {
          firstName = nameParts.slice(0, -1).join(' ');
          lastName = nameParts[nameParts.length - 1];
        }
      }

      // Update customer information with parsed data
      setCustomer(prev => ({
        ...prev,
        firstName: firstName || prev.firstName,
        lastName: lastName || prev.lastName,
        name: name || prev.name, // Keep name for backward compatibility
        email: email || prev.email,
        phone: phone || prev.phone,
        address: address || prev.address
      }));

      // Show success message
      window.alert('Contact information imported successfully!');
    } catch (error) {
      console.error('Error parsing contact text:', error);
      window.alert('Failed to parse contact information. Please check the format and try again.');
    }
  };

  const importContact = async () => {
    try {
      // Check if Contact Picker API is supported
      const hasContactsAPI = 'contacts' in navigator && 'ContactsManager' in window;
      const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      const isAndroid = /Android/.test(navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      console.log('Contact API Debug:', {
        hasContactsAPI,
        isChrome,
        isAndroid,
        isSafari,
        userAgent: navigator.userAgent
      });

      // The Contact Picker API is only available on Chrome/Edge on Android
      // Desktop Chrome doesn't support it yet
      if (!hasContactsAPI || !isAndroid) {
        // Show the contact import modal for all non-Android browsers
        setShowContactImport(true);
        return;
      }

      // Request permission and open contact picker (Android only)
      const contacts = await navigator.contacts.select(['name', 'email', 'tel', 'address'], {
        multiple: false
      });

      if (contacts && contacts.length > 0) {
        const contact = contacts[0];
        
        // Extract contact information
        const name = contact.name ? contact.name.join(' ') : '';
        const email = contact.email && contact.email.length > 0 ? contact.email[0] : '';
        const phone = contact.tel && contact.tel.length > 0 ? contact.tel[0] : '';
        
        // Parse name into firstName and lastName
        let firstName = '';
        let lastName = '';
        if (name) {
          const nameParts = name.trim().split(/\s+/);
          if (nameParts.length === 1) {
            firstName = nameParts[0];
            lastName = '';
          } else {
            firstName = nameParts.slice(0, -1).join(' ');
            lastName = nameParts[nameParts.length - 1];
          }
        }
        
        // Extract address (if available)
        let address = '';
        if (contact.address && contact.address.length > 0) {
          const addr = contact.address[0];
          const addressParts = [];
          if (addr.streetAddress) addressParts.push(addr.streetAddress);
          if (addr.city) addressParts.push(addr.city);
          if (addr.region) addressParts.push(addr.region);
          if (addr.postalCode) addressParts.push(addr.postalCode);
          if (addr.country) addressParts.push(addr.country);
          address = addressParts.join(', ');
        }

        // Update customer information
        setCustomer(prev => ({
          ...prev,
          firstName: firstName || prev.firstName,
          lastName: lastName || prev.lastName,
          name: name || prev.name, // Keep name for backward compatibility
          email: email || prev.email,
          phone: phone || prev.phone,
          address: address || prev.address
        }));

        // Show success message
        window.alert('Contact information imported successfully!');
      }
    } catch (error) {
      console.error('Error importing contact:', error);
      if (error.name === 'NotAllowedError') {
        window.alert('Contact access was denied. Please allow contact access to import contact information.');
      } else if (error.name === 'AbortError') {
        // User cancelled the contact picker, do nothing
        return;
      } else {
        window.alert('Failed to import contact. Please enter contact information manually.');
      }
    }
  };

  const allLineItems = structures.flatMap(s => s.lineItems);

  const computeTotalsForLineItems = (items) => {
    const exteriorInteriorItems = items.filter(item => {
      const windowType = windowTypes.find(w => w.name === item.windowType);
      return windowType?.category === 'exterior_interior';
    });
    const separateItems = items.filter(item => {
      const windowType = windowTypes.find(w => w.name === item.windowType);
      return windowType?.category === 'separate';
    });
    const exteriorInteriorSubtotal = exteriorInteriorItems.reduce((sum, item) => sum + item.price, 0);
    const separateItemsTotal = separateItems.reduce((sum, item) => sum + item.price, 0);
    let extraDirtyAmount = 0;
    if (settings.extraDirtyFee > 0) {
      if (settings.extraDirtyIsPercentage) extraDirtyAmount = exteriorInteriorSubtotal * (settings.extraDirtyFee / 100);
      else extraDirtyAmount = settings.extraDirtyFee;
    }
    let exteriorTotal = settings.showExterior ? exteriorInteriorSubtotal * (settings.exteriorPercentage / 100) : 0;
    let interiorTotal = settings.showInterior ? exteriorInteriorSubtotal * (settings.interiorPercentage / 100) : 0;
    if (settings.extraDirtyApplyTo === 'exterior') exteriorTotal += extraDirtyAmount;
    else if (settings.extraDirtyApplyTo === 'interior') interiorTotal += extraDirtyAmount;
    else if (settings.extraDirtyApplyTo === 'all' && (settings.showExterior || settings.showInterior)) {
      if (settings.showExterior && settings.showInterior) {
        exteriorTotal += extraDirtyAmount * (settings.exteriorPercentage / 100);
        interiorTotal += extraDirtyAmount * (settings.interiorPercentage / 100);
      } else if (settings.showExterior) exteriorTotal += extraDirtyAmount;
      else interiorTotal += extraDirtyAmount;
    }
    const total = exteriorTotal + interiorTotal + separateItemsTotal;
    return { exteriorTotal, interiorTotal, separateItems, total, extraDirtyAmount };
  };

  const calculateTotals = () => {
    return computeTotalsForLineItems(allLineItems);
  };

  // Async function for non-blocking Jobber sync
  const syncToJobberAsync = async (quoteToSync, userId) => {
    console.log('🔄 syncToJobberAsync called:', {
      quoteId: quoteToSync?.id,
      userId,
      hasCustomer: !!quoteToSync?.customer,
      customerName: quoteToSync?.customer?.name
    });
    
    try {
      console.log('🔐 Checking Jobber authentication...');
      const isAuthenticated = await JobberService.isAuthenticatedWithRefresh(userId);
      console.log('🔐 Authentication check result:', isAuthenticated);
      
      if (isAuthenticated) {
        console.log('✅ Authenticated, starting sync...');
        await handleSyncToJobber(quoteToSync);
        console.log('✅ Background Jobber sync completed successfully');
        setShowSyncSuccess(true);
        // Remove from queue if it was queued
        if (userId) {
          syncService.removeFromJobberSyncQueue(quoteToSync.id, userId);
        }
      } else {
        console.error('❌ Not authenticated with Jobber');
        throw new Error('Not authenticated with Jobber');
      }
    } catch (error) {
      console.error('❌ Background Jobber sync failed:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      // Queue for retry when Jobber reconnects
      if (userId && quoteToSync) {
        syncService.addToJobberSyncQueue(quoteToSync, userId);
        console.log('📋 Quote queued for Jobber sync when reconnected');
      }
      // Show non-blocking notification - quote is already saved
      setShowDisconnectionBubble(true);
      throw error; // Re-throw so caller knows it failed
    }
  };

  const handleSave = async () => {
    // Prevent multiple saves
    if (isSaving) {
      console.log('⏸️ Save already in progress, ignoring click');
      return;
    }
    
    console.log('💾 Save button clicked');
    
    setIsSaving(true);
    
    try {
      // Prepare quote data (structures + flattened lineItems for Firebase/iOS parity)
      const totals = calculateTotals();
      const lineItemsFlat = structures.flatMap(s => s.lineItems);
      const pricesUsedPerWindowType = (() => {
        const customerTypeObj = customerTypes.find(c => c.name === customer.type);
        const result = {};
        for (const item of lineItemsFlat) {
          const pricePerUnit = item.pricePerUnitOverride
            ?? customerTypeObj?.windowPrices?.[item.windowType]
            ?? windowTypes.find(w => w.name === item.windowType)?.price
            ?? 0;
          result[item.windowType] = pricePerUnit;
        }
        return Object.keys(result).length > 0 ? result : null;
      })();
      const newQuote = {
        id: quote?.id || Date.now(),
        quoteNumber: quote?.quoteNumber,
        customer,
        structures,
        lineItems: lineItemsFlat,
        settings,
        totals,
        notes: notes.trim() || null,
        pricesUsedPerWindowType,
        createdAt: quote?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      console.log('📝 Quote data prepared:', newQuote);
      
      // STEP 1: Save quote FIRST (critical path - must succeed)
      if (!onSave) {
        console.error('❌ onSave function is not defined!');
        alert('Error: Save handler is not available. Please refresh the page.');
        setIsSaving(false);
        return;
      }
      
      let savedQuote = null;
      let userId = null;
      try {
        console.log('🔄 Calling onSave...');
        savedQuote = await onSave(newQuote);
        userId = savedQuote?.userId || newQuote?.userId;
        if (userId) {
          setCurrentUserId(userId);
        }
        console.log('✅ Quote saved successfully', { quoteId: savedQuote?.id, userId });
        
        // Clear unsaved changes flag
        setHasUnsavedChanges(false);
      } catch (saveError) {
        console.error('❌ Quote save failed:', saveError);
        alert('Error saving quote: ' + (saveError.message || 'Please try again.'));
        setIsSaving(false);
        return; // Don't proceed if save fails
      }
      
      // STEP 2: Attempt Jobber sync ASYNCHRONOUSLY (non-blocking)
      // Don't await - let it run in background, don't block UI
      const isSyncAllEnabled = JobberService.isSyncAllEnabled();
      
      console.log('🔍 Checking sync all:', {
        isSyncAllEnabled,
        userId,
        localStorageValue: localStorage.getItem(`jobber_sync_all_${JobberService.getPlatform()}`),
        isAuthenticated: JobberService.isAuthenticated()
      });
      
      if (isSyncAllEnabled && userId) {
        console.log('🔄 Starting background Jobber sync...');
        // Use saved quote (with userId) for sync
        const quoteForSync = savedQuote || { ...newQuote, userId };
        // Fire and forget - don't block UI
        syncToJobberAsync(quoteForSync, userId).catch(error => {
          // Error already handled in syncToJobberAsync (queued and bubble shown)
          console.log('📋 Jobber sync queued for retry');
        });
      } else {
        console.log('⏸️ Sync all skipped:', { 
          isSyncAllEnabled, 
          hasUserId: !!userId,
          reason: !isSyncAllEnabled ? 'sync all disabled' : 'no userId'
        });
      }
      
      // STEP 3: Close the quote editor after successful save
      if (onCancel) {
        onCancel();
      }
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close without saving?');
      if (!confirmed) {
        return;
      }
    }
    if (onCancel) {
      onCancel();
    }
  };

  // Handler functions for sync feedback
  const handleSyncSuccessClose = () => {
    setShowSyncSuccess(false);
  };


  const handleDisconnectionBubbleClose = () => {
    setShowDisconnectionBubble(false);
  };

  const handleReconnectSuccess = async () => {
    const totals = calculateTotals();
    const currentQuote = {
      id: quote?.id || Date.now(),
      customer,
      structures,
      lineItems: structures.flatMap(s => s.lineItems),
      settings,
      totals,
      createdAt: quote?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      await handleSyncToJobber(currentQuote);
      console.log('✅ Quote synced to Jobber after reconnection');
      setShowSyncSuccess(true);
    } catch (error) {
      console.error('❌ Sync failed after reconnection:', error);
    }
  };

  const handleSyncToJobber = async (quoteToSync = null) => {
    console.log('🔄 handleSyncToJobber called:', {
      hasQuoteToSync: !!quoteToSync,
      quoteId: quoteToSync?.id || quote?.id
    });
    
    if (!JobberService.isAuthenticated()) {
      console.error('❌ Jobber not authenticated');
      window.alert('Please connect to Jobber first in the Settings page.');
      return;
    }

    try {
      const quoteData = quoteToSync || {
        id: quote?.id || Date.now(),
        customer,
        structures,
        lineItems: structures.flatMap(s => s.lineItems),
        settings,
        totals: calculateTotals(),
        createdAt: quote?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('📤 Syncing quote to Jobber:', {
        quoteId: quoteData.id,
        customerName: quoteData.customer?.name,
        lineItemsCount: quoteData.lineItems?.length
      });
      
      const result = await JobberService.syncQuoteToJobber(quoteData);
      console.log('✅ Sync result:', result);
      
      // Only show alert for manual sync, not auto-sync
      if (!quoteToSync) {
        window.alert('Quote successfully synced to Jobber!');
      }
    } catch (error) {
      console.error('❌ Error syncing to Jobber:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      if (!quoteToSync) {
        window.alert('Failed to sync to Jobber: ' + error.message);
      }
      throw error; // Re-throw for auto-sync error handling
    }
  };

  const totals = calculateTotals();

  return (
    <div className="min-h-screen bg-primary pb-20 mobile-app">
      <div className="form-header">
        <button onClick={handleCancel} className="flex items-center text-danger hover:opacity-80 touchable">
          <X className="w-5 h-5 mr-2" />
          <span className="hidden sm:inline">Cancel</span>
        </button>
        <h1 className="header-title text-lg sm:text-xl">
          {quote ? 'Edit Quote' : 'New Quote'}
        </h1>
        <div className="header-actions">
          <button 
            type="button"
            onClick={handleSave} 
            className="btn btn-primary"
            disabled={isSaving}
          >
            <Save className="w-4 h-4 sm:mr-2 inline" />
            <span className="hidden sm:inline">{isSaving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </div>

      <div className="paper-form">
        <section className="form-section">
          <h2 className="section-title mb-6">Customer Information</h2>
          
          {/* Customer Type selector and Import Contact button on same line (Issue #4) */}
          <div className="form-row" style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '12px' }}>
            <div className="form-group" style={{ flex: '1', marginBottom: 0 }}>
              <label className="form-label" htmlFor="customer-type">Customer Type</label>
              <select
                id="customer-type"
                value={customer.type}
                onChange={(e) => {
                  setHasUnsavedChanges(true);
                  setCustomer({...customer, type: e.target.value});
                }}
                className="form-select"
                style={{ height: '44px' }}
              >
                {customerTypes.map(type => (
                  <option key={type.id} value={type.name}>{type.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ visibility: 'hidden' }}>Import</label>
              <button
                onClick={importContact}
                className="btn btn-secondary flex items-center gap-2 text-sm touchable"
                style={{ height: '44px' }}
                title="Import from contacts"
              >
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Import Contact</span>
                <span className="sm:hidden">Import</span>
              </button>
            </div>
          </div>

          {/* Conditional name fields based on usesBusinessName (Feature #7) */}
          {(() => {
            const selectedCustomerType = customerTypes.find(c => c.name === customer.type);
            const usesBusinessName = selectedCustomerType?.usesBusinessName ?? false;
            
            if (usesBusinessName) {
              // Show single Business Name field
              return (
                <div className="form-row single">
                  <div className="form-group">
                    <label className="form-label" htmlFor="customer-business-name">Business Name</label>
                    <div className="form-input-with-icon">
                      <User className="form-input-icon" />
                      <input
                        type="text"
                        id="customer-business-name"
                        placeholder="Business Name"
                        value={customer.name || ''}
                        onChange={(e) => {
                          setHasUnsavedChanges(true);
                          const updatedCustomer = {
                            ...customer,
                            name: e.target.value,
                            firstName: '', // Clear firstName/lastName when using business name
                            lastName: ''
                          };
                          setCustomer(updatedCustomer);
                        }}
                        className="form-input"
                      />
                    </div>
                  </div>
                </div>
              );
            } else {
              // Show First Name and Last Name fields
              return (
                <div className="form-row" style={{ display: 'flex', gap: '16px', gridTemplateColumns: 'none', marginBottom: '12px' }}>
                  <div className="form-group" style={{ flex: '1', marginBottom: 0 }}>
                    <label className="form-label" htmlFor="customer-first-name">First Name</label>
                    <div className="form-input-with-icon">
                      <User className="form-input-icon" />
                      <input
                        type="text"
                        id="customer-first-name"
                        placeholder="John & Jane"
                        value={customer.firstName || ''}
                        onChange={(e) => {
                          setHasUnsavedChanges(true);
                          const updatedCustomer = {
                            ...customer,
                            firstName: e.target.value,
                            name: `${e.target.value} ${customer.lastName || ''}`.trim() // Keep name for backward compatibility
                          };
                          setCustomer(updatedCustomer);
                        }}
                        className="form-input"
                      />
                    </div>
                  </div>
                  <div className="form-group" style={{ flex: '1', marginBottom: 0 }}>
                    <label className="form-label" htmlFor="customer-last-name">Last Name</label>
                    <input
                      type="text"
                      id="customer-last-name"
                      placeholder="Doe"
                      value={customer.lastName || ''}
                      onChange={(e) => {
                        setHasUnsavedChanges(true);
                        const updatedCustomer = {
                          ...customer,
                          lastName: e.target.value,
                          name: `${customer.firstName || ''} ${e.target.value}`.trim() // Keep name for backward compatibility
                        };
                        setCustomer(updatedCustomer);
                      }}
                      className="form-input"
                    />
                  </div>
                </div>
              );
            }
          })()}
          <div className="form-row single">
            <div className="form-group">
              <div className="form-input-with-icon">
                <MapPin className="form-input-icon" />
                <input
                  type="text"
                  id="customer-address"
                  placeholder="Enter address"
                  value={customer.address}
                  onChange={(e) => {
                    setHasUnsavedChanges(true);
                    setCustomer({...customer, address: e.target.value});
                  }}
                  className="form-input"
                />
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <div className="form-input-with-icon">
                <Phone className="form-input-icon" />
                <input
                  type="tel"
                  id="customer-phone"
                  placeholder="Enter phone number"
                  value={customer.phone}
                  onChange={(e) => {
                    setHasUnsavedChanges(true);
                    setCustomer({...customer, phone: e.target.value});
                  }}
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-group">
              <div className="form-input-with-icon">
                <Mail className="form-input-icon" />
                <input
                  type="email"
                  id="customer-email"
                  placeholder="Enter email address"
                  value={customer.email}
                  onChange={(e) => {
                    setHasUnsavedChanges(true);
                    setCustomer({...customer, email: e.target.value});
                  }}
                  className="form-input"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="form-section">
          <div className="mb-4">
            <h2 className="section-title">Window Services</h2>
            <p className="text-tertiary text-sm mt-1">Add one or more buildings; each can have its own window types and counts.</p>
          </div>

          {structures.map((structure, sIdx) => (
            <div key={structure.id} className="mb-6 p-4 rounded-xl border border-primary bg-quaternary/30">
              <div className="flex items-center justify-between gap-2 mb-3">
                <input
                  type="text"
                  value={structure.name}
                  onChange={(e) => setStructureName(sIdx, e.target.value)}
                  placeholder="e.g. Main House, Guest House"
                  className="form-input flex-1 font-medium"
                />
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => { setAddLineItemToStructureIdx(sIdx); setShowWindowTypePopup(true); }}
                    className="btn btn-secondary p-2 touchable"
                    title="Add window type to this building"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                  {structures.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeStructure(sIdx)}
                      className="btn btn-icon text-danger touchable"
                      title="Remove building"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="window-types">
                {structure.lineItems.map(item => (
                  <div
                    key={item.id}
                    className={`window-type-item ${draggedLineItemId === item.id ? 'opacity-60' : ''} ${dragOverLineItemId === item.id ? 'ring-2 ring-accent-primary' : ''}`}
                    data-line-item-id={item.id}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (draggedLineItemId && draggedLineItemId !== item.id) setDragOverLineItemId(item.id);
                    }}
                    onDragLeave={() => setDragOverLineItemId(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      const droppedId = e.dataTransfer.getData('text/plain');
                      if (droppedId) handleLineItemDrop(sIdx, item.id, droppedId);
                      setDragOverLineItemId(null);
                    }}
                  >
                    <div className="window-type-info flex items-center gap-2">
                      <span
                        className="text-tertiary cursor-grab active:cursor-grabbing touch-none shrink-0"
                        title="Drag to reorder"
                        draggable
                        onDragStart={(e) => {
                          e.stopPropagation();
                          setDraggedLineItemId(item.id);
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', item.id);
                        }}
                        onDragEnd={() => { setDraggedLineItemId(null); setDragOverLineItemId(null); }}
                      >
                        <GripVertical className="w-4 h-4" />
                      </span>
                      <div className="window-type-name">{item.windowType}</div>
                    </div>
                    <div className="window-type-details">
                      <input
                        type="text"
                        placeholder="Counts (e.g. 8 7 9 12)"
                        value={item.counts}
                        onChange={(e) => updateLineItem(sIdx, item.id, 'counts', e.target.value)}
                        className="quantity-input"
                      />
                      <div className="price-display">${item.price.toFixed(2)}</div>
                      <button
                        type="button"
                        onClick={() => {
                          setEditPriceFor({ structureIdx: sIdx, item });
                          setEditPriceInput(item.pricePerUnitOverride != null ? String(item.pricePerUnitOverride) : '');
                        }}
                        className="btn btn-icon text-accent-primary touchable"
                        title="Edit price for this quote"
                      >
                        <DollarSign className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeLineItem(sIdx, item.id)}
                        className="delete-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addStructure}
            className="add-window-btn w-full border-2 border-dashed border-primary/50 hover:bg-quaternary/50"
          >
            <Plus className="w-5 h-5" />
            Add building
          </button>
        </section>

        <section className="form-section">
          <h2 className="section-title">Quote Settings</h2>
          
          {/* Exterior/Interior Percentages */}
          <div className="settings-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="exterior-percentage">Exterior Percentage</label>
              <div className="percentage-input">
                <input
                  type="text"
                  id="exterior-percentage"
                  value={settings.exteriorPercentage}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*$/.test(value)) {
                      const numValue = value === '' ? 0 : parseInt(value);
                      setHasUnsavedChanges(true);
                      setSettings({
                        ...settings, 
                        exteriorPercentage: numValue, 
                        interiorPercentage: Math.max(0, 100 - numValue)
                      });
                    }
                  }}
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="interior-percentage">Interior Percentage</label>
              <div className="percentage-input">
                <input
                  type="text"
                  id="interior-percentage"
                  value={settings.interiorPercentage}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*$/.test(value)) {
                      const numValue = value === '' ? 0 : parseInt(value);
                      setHasUnsavedChanges(true);
                      setSettings({
                        ...settings, 
                        interiorPercentage: numValue, 
                        exteriorPercentage: Math.max(0, 100 - numValue)
                      });
                    }
                  }}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <div className="dropdown-container">
              <button
                type="button"
                onClick={() => setShowExtraDirtyOptions(!showExtraDirtyOptions)}
                className="dropdown-toggle"
              >
                <span className="dropdown-title">Extra Dirty Fee</span>
                <div className={`dropdown-arrow ${showExtraDirtyOptions ? 'open' : ''}`}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4"/>
                  </svg>
                </div>
              </button>
              
              {showExtraDirtyOptions && (
                <div className="dropdown-content">
                  <div className="form-group">
                    <label className="form-label" htmlFor="extra-dirty-fee">Fee Amount</label>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2">
                      {/* Toggle and Input Row */}
                      <div className="flex items-center gap-2 flex-1">
                        <div className="relative bg-gray-100 rounded-full p-1 flex items-center flex-shrink-0">
                          <div className={`absolute top-1 bottom-1 rounded-full transition-all duration-300 ${
                            settings.extraDirtyIsPercentage 
                              ? 'left-1/2 right-1 bg-gradient-to-r from-orange-500 to-orange-600' 
                              : 'left-1 right-1/2 bg-gradient-to-r from-green-500 to-green-600'
                          }`} />
                          <button
                            onClick={() => {
                              setHasUnsavedChanges(true);
                              setSettings({...settings, extraDirtyIsPercentage: false});
                            }}
                            className={`relative z-10 px-3 py-1 rounded-full text-xs font-bold transition-all duration-300 ${
                              !settings.extraDirtyIsPercentage 
                                ? 'text-white text-sm transform scale-110' 
                                : 'text-gray-600 text-xs'
                            }`}
                          >
                            $
                          </button>
                          <button
                            onClick={() => {
                              setHasUnsavedChanges(true);
                              setSettings({...settings, extraDirtyIsPercentage: true});
                            }}
                            className={`relative z-10 px-3 py-1 rounded-full text-xs font-bold transition-all duration-300 ${
                              settings.extraDirtyIsPercentage 
                                ? 'text-white text-sm transform scale-110' 
                                : 'text-gray-600 text-xs'
                            }`}
                          >
                            %
                          </button>
                        </div>
                        <input
                          type="text"
                          id="extra-dirty-fee"
                          value={settings.extraDirtyFee}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              setSettings({...settings, extraDirtyFee: value === '' ? 0 : parseFloat(value) || 0});
                            }
                          }}
                          className="form-input flex-1 sm:flex-initial"
                          style={{ minWidth: '0', maxWidth: 'none' }}
                        />
                      </div>
                      {/* Apply To Row */}
                      <div className="flex items-center gap-2 flex-1 sm:flex-initial">
                        <span className="text-sm text-tertiary whitespace-nowrap">Apply to:</span>
                        <select
                          id="apply-fee-to"
                          value={settings.extraDirtyApplyTo}
                          onChange={(e) => {
                            setHasUnsavedChanges(true);
                            setSettings({...settings, extraDirtyApplyTo: e.target.value});
                          }}
                          className="form-select flex-1 sm:flex-initial"
                          style={{ minWidth: '0', maxWidth: 'none' }}
                        >
                          <option value="all">All Items</option>
                          <option value="exterior">Exterior Windows Only</option>
                          <option value="interior">Interior Windows Only</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
        <section className="form-section">
          <h2 className="section-title">Quote Preview</h2>
          <div className="preview-items">
            {structures.map((structure) => {
              const stTotals = computeTotalsForLineItems(structure.lineItems);
              const separateItems = stTotals.separateItems || [];
              const separateByType = separateItems.reduce((acc, item) => {
                const name = item.windowType;
                if (!acc[name]) acc[name] = { windowType: name, price: 0 };
                acc[name].price += item.price;
                return acc;
              }, {});
              const stTotal = stTotals.exteriorTotal + stTotals.interiorTotal + separateItems.reduce((s, i) => s + i.price, 0);
              return (
                <div key={structure.id} className="rounded-xl border border-primary/20 bg-quaternary/40 p-4 mb-4">
                  <div className="preview-item font-medium mb-2">
                    <span>{structure.name}</span>
                    <span>Total Price: ${stTotal.toFixed(2)}</span>
                  </div>
                  {settings.showExterior && stTotals.exteriorTotal > 0 && (
                    <div className="preview-item pl-2 text-tertiary">
                      <span>Exterior</span>
                      <span>${stTotals.exteriorTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {settings.showInterior && stTotals.interiorTotal > 0 && (
                    <div className="preview-item pl-2 text-tertiary">
                      <span>Interior</span>
                      <span>${stTotals.interiorTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {Object.values(separateByType).map(row => (
                    <div key={row.windowType} className="preview-item pl-2 text-tertiary">
                      <span>{row.windowType}</span>
                      <span>${row.price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              );
            })}
            <div className="preview-item border-t border-primary/20 mt-2 pt-2">
              <span className="font-medium">Exterior Total</span>
              <span>${totals.exteriorTotal.toFixed(2)}</span>
            </div>
            <div className="preview-item">
              <span className="font-medium">Interior Total</span>
              <span>${totals.interiorTotal.toFixed(2)}</span>
            </div>
            {(() => {
              const separateByType = (totals.separateItems || []).reduce((acc, item) => {
                const name = item.windowType;
                if (!acc[name]) acc[name] = { windowType: name, price: 0 };
                acc[name].price += item.price;
                return acc;
              }, {});
              return Object.values(separateByType).map(row => (
                <div key={row.windowType} className="preview-item">
                  <span className="font-medium">{row.windowType} Total</span>
                  <span>${row.price.toFixed(2)}</span>
                </div>
              ));
            })()}
            {totals.extraDirtyAmount > 0 && (
              <div className="preview-item">
                <span>Extra Dirty Fee</span>
                <span>${totals.extraDirtyAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="preview-item total">
              <span>Total</span>
              <span>${totals.total.toFixed(2)}</span>
            </div>
          </div>
        </section>

        {/* Notes section (Feature #5) */}
        <section className="form-section">
          <h2 className="section-title">Notes</h2>
          <textarea
            value={notes}
            onChange={(e) => {
              setHasUnsavedChanges(true);
              setNotes(e.target.value);
            }}
            placeholder="e.g. no doors [construction], pay extra attention to..."
            className="form-input w-full min-h-[100px] max-h-[180px] resize-y"
            rows={4}
          />
        </section>
      </div>

      {/* Edit Price Modal (Feature #2) */}
      {editPriceFor && (() => {
        const { structureIdx, item } = editPriceFor;
        const defaultPrice = getBasePriceForLineItem({ ...item, pricePerUnitOverride: undefined });
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-secondary rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-lg font-bold text-primary mb-2">Edit Price</h3>
              <p className="text-sm text-tertiary mb-4">
                Price per unit for {item.windowType}. Default for this customer type: ${defaultPrice.toFixed(2)}
              </p>
              <input
                type="text"
                inputMode="decimal"
                value={editPriceInput}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d*$/.test(v)) setEditPriceInput(v);
                }}
                placeholder={defaultPrice.toFixed(2)}
                className="form-input w-full mb-4"
              />
              {item.pricePerUnitOverride != null && (
                <button
                  type="button"
                  onClick={() => saveEditPrice(structureIdx, item.id, null)}
                  className="text-sm text-accent-primary mb-4 block"
                >
                  Use default price
                </button>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setEditPriceFor(null); setEditPriceInput(''); }}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const trimmed = editPriceInput.trim();
                    if (trimmed === '') {
                      saveEditPrice(structureIdx, item.id, null);
                    } else {
                      const val = parseFloat(trimmed);
                      if (!isNaN(val) && val >= 0) saveEditPrice(structureIdx, item.id, val);
                    }
                  }}
                  className="btn btn-primary flex-1"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Window Type Selection Popup */}
      {showWindowTypePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-secondary rounded-2xl p-6 max-w-md w-full max-h-96 overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-primary">Select Window Type</h3>
              <button
                onClick={() => setShowWindowTypePopup(false)}
                className="text-tertiary hover:text-primary touchable"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {windowTypes.map(type => {
                // Calculate price based on current customer type (Issue #1 fix)
                const customerTypeObj = customerTypes.find(c => c.name === customer.type);
                const priceForType = customerTypeObj?.windowPrices?.[type.name] ?? type.price;
                
                return (
                  <button
                    key={type.id}
                    onClick={() => {
                      if (addLineItemToStructureIdx !== null) addLineItem(addLineItemToStructureIdx, type.name);
                      setShowWindowTypePopup(false);
                      setAddLineItemToStructureIdx(null);
                    }}
                    className="window-type-select-btn w-full text-left p-3 rounded-xl border border-primary hover:bg-quaternary touchable"
                  >
                    <div className="font-medium text-primary">{type.name}</div>
                    <div className="text-sm text-tertiary">${priceForType.toFixed(2)} per window</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Contact Import Modal */}
      {showContactImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="card w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-primary">Import Contact</h3>
              <button
                onClick={() => setShowContactImport(false)}
                className="btn btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-tertiary text-sm mb-3">
                Import contact information from your device's contacts app.
              </p>
              <div className="bg-tertiary rounded-xl p-3 border border-primary mb-3">
                <p className="text-primary text-sm font-medium mb-2">Instructions:</p>
                <ol className="text-tertiary text-sm space-y-1 list-decimal list-inside">
                  <li>Open your Contacts app</li>
                  <li>Find the contact you want</li>
                  <li>Copy the contact information</li>
                  <li>Paste it below in this format:</li>
                </ol>
              </div>
              
              <div className="bg-tertiary rounded-xl p-3 border border-primary mb-4">
                <p className="text-primary text-sm font-medium mb-2">Format:</p>
                <pre className="text-tertiary text-xs whitespace-pre-line">
{`Name: John Smith
Email: john@example.com
Phone: (555) 123-4567
Address: 123 Main St, City, State 12345`}
                </pre>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-primary mb-2">
                Paste Contact Information:
              </label>
              <textarea
                value={contactImportText}
                onChange={(e) => setContactImportText(e.target.value)}
                placeholder="Paste contact info here..."
                className="input-field w-full h-32 resize-none focusable"
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowContactImport(false)}
                className="btn btn-danger flex-1"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (contactImportText.trim()) {
                    parseContactText(contactImportText);
                    setShowContactImport(false);
                    setContactImportText('');
                  } else {
                    window.alert('Please paste contact information first.');
                  }
                }}
                className="btn btn-primary flex-1"
              >
                Import Contact
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Feedback Components */}
      <SyncSuccessPopup 
        show={showSyncSuccess} 
        onClose={handleSyncSuccessClose} 
      />
      
      <DisconnectionBubble 
        show={showDisconnectionBubble} 
        onClose={handleDisconnectionBubbleClose}
        onReconnectSuccess={handleReconnectSuccess}
        userId={currentUserId}
      />
    </div>
  );
};

export default QuoteCreator;
